import React, { useState } from "react";
import { Search, Loader2, Music2, Check, X, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PreviewButton } from "@/components/PreviewButton";
import { stopPreview } from "@/lib/audio";

export const ItunesSearchModal = ({ open, onClose, onSelect, momentName, initialQuery = "" }) => {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [manual, setManual] = useState("");

  const doSearch = async (e) => {
    e && e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/itunes/search`, { params: { q } });
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    stopPreview();
    setConfirm(null);
    onClose();
  };

  const pick = (track) => {
    onSelect({
      track_id: track.track_id,
      track_name: track.track_name,
      artist_name: track.artist_name,
      artwork: track.artwork,
      duration: track.duration,
      preview_url: track.preview_url,
      link: track.link,
    });
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="itunes-search-modal" className="max-w-lg gap-0 p-0">
        <DialogHeader className="border-b border-[color:var(--brand-border)] p-5">
          <DialogTitle className="font-serif text-xl">Buscar música</DialogTitle>
          <p className="text-sm text-[color:var(--brand-muted)]">{momentName}</p>
        </DialogHeader>

        <div className="p-5">
          <form onSubmit={doSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--brand-muted)]" />
              <Input
                data-testid="itunes-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nome da música ou artista"
                className="h-11 pl-9"
              />
            </div>
            <Button type="submit" data-testid="itunes-search-submit" disabled={loading} className="h-11 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
            </Button>
          </form>

          <div className="mt-4 max-h-[46vh] space-y-2 overflow-y-auto">
            {loading && (
              <div className="py-10 text-center text-sm text-[color:var(--brand-muted)]">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Procurando...
              </div>
            )}
            {!loading && searched && results.length === 0 && (
              <div data-testid="itunes-empty" className="rounded-xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-5 text-center">
                <p className="text-sm text-[color:var(--brand-muted)]">Sem resultados. Tente pelo artista ou parte do nome, ou cole um link manualmente abaixo.</p>
              </div>
            )}
            {!loading &&
              results.map((t) => (
                <div
                  key={t.track_id}
                  data-testid={`itunes-result-${t.track_id}`}
                  className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors cursor-pointer ${
                    confirm?.track_id === t.track_id
                      ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)]"
                      : "border-[color:var(--brand-border)] bg-white hover:bg-[color:var(--brand-paper)]"
                  }`}
                  onClick={() => setConfirm(t)}
                >
                  {t.artwork ? (
                    <img src={t.artwork} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[color:var(--brand-paper)]">
                      <Music2 className="h-5 w-5 text-[color:var(--brand-muted)]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[color:var(--brand-graphite)]">{t.track_name}</p>
                    <p className="truncate text-xs text-[color:var(--brand-muted)]">{t.artist_name}{t.duration ? ` · ${t.duration}` : ""}</p>
                  </div>
                  <PreviewButton url={t.preview_url} testId={`itunes-preview-${t.track_id}`} />
                </div>
              ))}
          </div>

          {confirm && (
            <div data-testid="itunes-confirm" className="mt-4 rounded-xl border border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] p-4">
              <p className="text-sm font-medium text-[color:var(--brand-graphite)]">
                É essa música? <span className="font-semibold">{confirm.track_name}</span> — {confirm.artist_name}
              </p>
              <div className="mt-3 flex gap-2">
                <Button data-testid="itunes-confirm-yes" onClick={() => pick(confirm)} className="h-10 flex-1 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
                  <Check className="mr-1.5 h-4 w-4" /> Sim, é essa
                </Button>
                <Button data-testid="itunes-confirm-no" variant="outline" onClick={() => setConfirm(null)} className="h-10 flex-1">
                  <X className="mr-1.5 h-4 w-4" /> Não, procurar outra
                </Button>
              </div>
            </div>
          )}

          <div className="mt-5 border-t border-[color:var(--brand-border)] pt-4">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-[color:var(--brand-muted)]">
              <Link2 className="h-3.5 w-3.5" /> Não encontrou? Cole um link de referência
            </p>
            <div className="flex gap-2">
              <Input
                data-testid="itunes-manual-link"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="https://youtube.com/... ou Spotify/Apple"
                className="h-10"
              />
              <Button
                data-testid="itunes-manual-save"
                variant="outline"
                disabled={!manual.trim()}
                onClick={() => {
                  onSelect({ manual_only: true, link: manual.trim() });
                  close();
                }}
                className="h-10"
              >
                Usar link
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
